// Config registry for tunables outside content tables.
// Release builds freeze the registry; dev builds allow hot reload.
// See GDD Appendix A §A.5.

export interface ConfigEntry {
  path: string;
  type: 'number' | 'boolean' | 'string';
  default: unknown;
  min?: number;
  max?: number;
  hot_reload: boolean;
  owner: string;
}

const defaults: ConfigEntry[] = [
  // Simulation
  { path: 'sim.step_rate_hz', type: 'number', default: 60, min: 30, max: 120, hot_reload: false, owner: 'performance' },
  { path: 'sim.max_catchup_steps', type: 'number', default: 5, min: 1, max: 10, hot_reload: false, owner: 'performance' },
  { path: 'sim.arena_size', type: 'number', default: 200, min: 100, max: 400, hot_reload: false, owner: 'arena' },

  // Player
  { path: 'player.max_hp', type: 'number', default: 100, min: 1, max: 1000, hot_reload: false, owner: 'game-feel' },
  { path: 'player.move_speed', type: 'number', default: 8, min: 1, max: 32, hot_reload: true, owner: 'game-feel' },
  { path: 'player.accel', type: 'number', default: 64, min: 1, max: 256, hot_reload: true, owner: 'game-feel' },
  { path: 'player.brake', type: 'number', default: 96, min: 1, max: 256, hot_reload: true, owner: 'game-feel' },
  { path: 'player.reverse_decel', type: 'number', default: 128, min: 1, max: 512, hot_reload: true, owner: 'game-feel' },
  { path: 'player.dash_speed', type: 'number', default: 32, min: 1, max: 128, hot_reload: true, owner: 'game-feel' },
  { path: 'player.dash_duration', type: 'number', default: 0.19, min: 0.05, max: 1, hot_reload: true, owner: 'game-feel' },
  { path: 'player.dash_iframes', type: 'number', default: 0.25, min: 0.05, max: 1, hot_reload: true, owner: 'game-feel' },
  { path: 'player.dash_cooldown', type: 'number', default: 2.0, min: 0.5, max: 10, hot_reload: true, owner: 'game-feel' },
  { path: 'player.mercy_window', type: 'number', default: 0.5, min: 0, max: 2, hot_reload: true, owner: 'game-feel' },
  { path: 'player.contact_knockback', type: 'number', default: 4, min: 0, max: 16, hot_reload: true, owner: 'game-feel' },
  { path: 'player.knockback_decay', type: 'number', default: 40, min: 1, max: 200, hot_reload: true, owner: 'game-feel' },

  // Targeting
  { path: 'targeting.reacquire_interval', type: 'number', default: 6, min: 1, max: 30, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.focus_reacquire_interval', type: 'number', default: 3, min: 1, max: 15, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.hysteresis_margin', type: 'number', default: 0.15, min: 0, max: 0.5, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.min_hold_ticks', type: 'number', default: 15, min: 0, max: 60, hot_reload: true, owner: 'targeting' },
  { path: 'targeting.focus_cone_angle', type: 'number', default: 30, min: 5, max: 90, hot_reload: true, owner: 'targeting' },

  // Spawn
  { path: 'spawn.safety_radius', type: 'number', default: 12, min: 4, max: 40, hot_reload: false, owner: 'director' },
  { path: 'spawn.in_view_telegraph', type: 'number', default: 1.5, min: 0.5, max: 5, hot_reload: false, owner: 'director' },

  // Waves (The Tower-style cycle: quiet intermission → assault → mop-up)
  { path: 'waves.first_intermission_s', type: 'number', default: 0.5, min: 0, max: 30, hot_reload: false, owner: 'director' },
  { path: 'waves.intermission_s', type: 'number', default: 8, min: 0, max: 60, hot_reload: false, owner: 'director' },
  { path: 'waves.assault_s', type: 'number', default: 20, min: 5, max: 120, hot_reload: false, owner: 'director' },
  { path: 'waves.mop_up_s', type: 'number', default: 18, min: 5, max: 120, hot_reload: false, owner: 'director' },
  { path: 'waves.boss_every', type: 'number', default: 5, min: 2, max: 20, hot_reload: false, owner: 'director' },
  { path: 'waves.base_budget', type: 'number', default: 95, min: 0, max: 500, hot_reload: false, owner: 'director' },
  { path: 'waves.budget_per_wave', type: 'number', default: 38, min: 0, max: 100, hot_reload: false, owner: 'director' },

  // Visual
  { path: 'visual.bloom_intensity', type: 'number', default: 1.0, min: 0, max: 2, hot_reload: true, owner: 'vfx' },
  { path: 'visual.particle_emission', type: 'number', default: 1.0, min: 0, max: 2, hot_reload: true, owner: 'vfx' },
  { path: 'visual.screen_shake', type: 'number', default: 1.0, min: 0, max: 2, hot_reload: true, owner: 'vfx' },
  { path: 'visual.reduced_flashes', type: 'boolean', default: false, hot_reload: true, owner: 'vfx' },

  // Performance
  { path: 'perf.degradation_enabled', type: 'boolean', default: true, hot_reload: true, owner: 'performance' },
  { path: 'perf.max_enemies', type: 'number', default: 512, min: 64, max: 2048, hot_reload: false, owner: 'performance' },
  { path: 'perf.max_projectiles', type: 'number', default: 1536, min: 256, max: 4096, hot_reload: false, owner: 'performance' },
  { path: 'perf.max_particles', type: 'number', default: 5120, min: 512, max: 8192, hot_reload: false, owner: 'performance' },

  // Mobile performance profile caps (active when video.mobile_performance is on).
  { path: 'perf.mobile_max_enemies', type: 'number', default: 150, min: 32, max: 512, hot_reload: false, owner: 'performance' },
  { path: 'perf.mobile_max_particles', type: 'number', default: 3072, min: 256, max: 8192, hot_reload: false, owner: 'performance' },
  { path: 'perf.mobile_bloom_scale', type: 'number', default: 0.7, min: 0, max: 1, hot_reload: true, owner: 'performance' },

  // Dev
  { path: 'dev.overlay', type: 'boolean', default: false, hot_reload: true, owner: 'qa' },
  { path: 'dev.god_mode', type: 'boolean', default: false, hot_reload: true, owner: 'qa' }
];

class ConfigRegistry {
  private values = new Map<string, unknown>();
  private readonly frozen: boolean;

  constructor() {
    for (const entry of defaults) {
      this.values.set(entry.path, entry.default);
    }
    this.frozen = typeof __DEV__ !== 'undefined' && !__DEV__;
    if (this.frozen) {
      Object.freeze(this.values);
    }
  }

  getNumber(path: string): number {
    const v = this.values.get(path);
    if (typeof v !== 'number') throw new Error(`Config ${path} is not a number`);
    return v;
  }

  getBoolean(path: string): boolean {
    const v = this.values.get(path);
    if (typeof v !== 'boolean') throw new Error(`Config ${path} is not a boolean`);
    return v;
  }

  getString(path: string): string {
    const v = this.values.get(path);
    if (typeof v !== 'string') throw new Error(`Config ${path} is not a string`);
    return v;
  }

  set(path: string, value: unknown): void {
    if (this.frozen) throw new Error(`Config registry is frozen in release builds`);
    const entry = defaults.find(e => e.path === path);
    if (!entry) throw new Error(`Unknown config path: ${path}`);
    if (typeof value !== entry.type) throw new Error(`Config ${path} expects ${entry.type}`);
    if (entry.type === 'number') {
      const n = value as number;
      if (entry.min !== undefined && n < entry.min) return;
      if (entry.max !== undefined && n > entry.max) return;
    }
    this.values.set(path, value);
  }
}

export const cfg = new ConfigRegistry();
